// @ts-expect-error citeproc-js has no types
import CSL from 'citeproc-js';
import { CitationData, Name } from 'types/fields';
import locale from 'lib/en-US-locale';
import CitationTypes from 'types/citation-types';
import outputFormats from 'types/output-formats';
import citationForms from 'types/citation-forms';

// TODO consider creating a singleton for this module
// https://citeproc-js.readthedocs.io/en/latest/running.html?highlight=previewcitationcluster#special-citation-forms
export type CiteProcModifiers = {
  locator?: string;
  label?: 'page';
  'suppress-author'?: boolean;
  'author-only'?: boolean;
  // there's more but citeproc documentation bad
};

// return type for citeproc.rebuildProccessorState: https://github.com/Juris-M/citeproc-js-docs/blob/master/original.txt
export type CiteProcRebuildProcessorOutput = [
  string, // citation id
  number, // note index
  string, // in-text citation
][];

export enum CitationLocation {
  Intext,
  Footnote,
}

export type CitationDataArray = {
  citationType: CitationTypes;
  data: CitationData;
}[];
export type ProcessedCitationData = {
  bibliography: any;
};

let citationDataOutside: CitationDataArray = [];
let currentStyle: { name: string; xml: string };

const retrieveItemOutside = (id: string): Record<string, any> => {
  const foundCitation = citationDataOutside.find(
    (citation) => citation.data.id === id,
  );
  if (foundCitation) {
    return foundCitation.data;
  }
  return {};
};

const authorDoesNotExist = (author: Name): boolean => {
  return !author.given && !author.family && !author.literal;
};

const generateCiteproc = (citationXML: string): Record<string, any> => {
  const citeproc = new CSL.Engine(
    {
      retrieveItem: (id: string) => {
        return retrieveItemOutside(id);
      },
      retrieveLocale: () => {
        return locale;
      },
    },
    citationXML,
  );

  // Ensure page_mangler and year_mangler are properly initialized
  // This fixes issues with some citation styles or engine initialization
  if (citeproc.fun && !citeproc.fun.page_mangler) {
    citeproc.fun.page_mangler = function(str: string) { return str; };
  }
  if (citeproc.fun && !citeproc.fun.year_mangler) {
    citeproc.fun.year_mangler = function(str: string) { return str; };
  }

  return citeproc;
};

let citeproc: Record<string, any>;

export const isCiteprocInitialized = (): boolean => {
  return typeof citeproc !== 'undefined';
};

// returns citation location (in-text or footnote) to allow caller to update the store
export const setCSLCitationStyle = (citationStyle: {
  name: string;
  xml: string;
}): void => {
  try {
    currentStyle = citationStyle;
    citeproc = generateCiteproc(currentStyle.xml);

    // Validate that the engine was properly initialized
    if (!citeproc || !citeproc.fun) {
      throw new Error('Failed to initialize citeproc engine with the provided citation style');
    }
  } catch (error) {
    console.error('Error setting citation style:', error);
    // Fallback to a basic initialization if something goes wrong
    currentStyle = citationStyle;
    // Try once more with error logging
    try {
      citeproc = new CSL.Engine(
        {
          retrieveItem: (id: string) => retrieveItemOutside(id),
          retrieveLocale: () => locale,
        },
        citationStyle.xml,
      );
      // Manually ensure required functions exist
      if (citeproc.fun) {
        citeproc.fun.page_mangler = citeproc.fun.page_mangler || function(str: string) { return str; };
        citeproc.fun.year_mangler = citeproc.fun.year_mangler || function(str: string) { return str; };
      }
    } catch (innerError) {
      console.error('Failed to recover from citation style initialization error:', innerError);
      throw innerError;
    }
  }
};

// citeproc needs to be setup with specifically formatted citations before it can create bibs
export const setupCiteProc = (
  citationData: CitationDataArray,
  outputFormat: outputFormats = outputFormats.html,
): string[] => {
  citationDataOutside = processCitationDataForCiteProc(citationData);
  const citationIDs = citationData.map((citation) => citation.data.id);

  try {
    // Ensure citeproc is initialized
    if (!citeproc) {
      throw new Error('Citeproc engine not initialized. Please set a citation style first.');
    }

    citeproc.deleteAll();
    citeproc.setOutputFormat(outputFormat);

    // Additional safety check for page_mangler before updateItems
    if (citeproc.fun && !citeproc.fun.page_mangler) {
      citeproc.fun.page_mangler = function(str: string) { return str; };
    }

    citeproc.updateItems(citationIDs);
  } catch (error) {
    console.error('Error in setupCiteProc:', error);
    // If there's an error with page_mangler, provide a more specific message
    if (error instanceof TypeError && error.message.includes('page_mangler')) {
      console.error('Page mangler initialization issue detected. Attempting recovery...');
      // Try to reinitialize the citeproc engine
      if (currentStyle) {
        citeproc = generateCiteproc(currentStyle.xml);
        citeproc.deleteAll();
        citeproc.setOutputFormat(outputFormat);
        citeproc.updateItems(citationIDs);
      }
    } else {
      throw error; // Re-throw if it's a different error
    }
  }

  return citationIDs;
};

export const clearCiteProc = (): void => {
  // @ts-expect-error TODO remove ts ignore if you make this module a singleton
  citeproc = undefined;
};

const getCitations = (
  citationData: CitationDataArray,
  outputFormat: outputFormats = outputFormats.html,
): ProcessedCitationData => {
  const citationIDs = setupCiteProc(citationData, outputFormat);
  let bib = citeproc.makeBibliography();

  // bib doesn't exist for some styles e.g. 'Bluebook' styles
  if (!bib) {
    bib = [{ entry_ids: citationIDs.map((id) => [id]) }];
  }

  return { bibliography: bib };
};

// Deep clones citation data to avoid mutating state redux state object, and
// modifies to fit citeproc api better.
const processCitationDataForCiteProc = (
  citationDataArray: CitationDataArray,
): CitationDataArray => {
  const newCitationDataArray = citationDataArray.map((citation) => {
    const citationData = citation.data;
    const citationDataAuthorsProcessed = citationData.author
      ? { ...citationData, author: processAuthors(citationData.author) }
      : citationData;
    const keys = Object.keys(citationForms[citation.citationType].fields);
    const obj: any = {};

    for (const key of keys) {
      if (citationDataAuthorsProcessed.hasOwnProperty(key)) {
        obj[key] = (citationDataAuthorsProcessed as any)[key];
      }
    }
    const filteredCitationData = obj as CitationData;

    filteredCitationData.id = citation.data.id;
    filteredCitationData.type = citation.data.type;
    return { ...citation, data: filteredCitationData };
  });

  return newCitationDataArray;
};

// Checks through authors and removes those that have no fields
// Note: this processing is needed for citeproc to fully support authorless in-text citations.
const processAuthors = (authors: Name[]): Name[] => {
  return authors.filter((author) => {
    return !authorDoesNotExist(author);
  });
};

// Returns extracted in-text citations from citeproc bibliography object.
// citeproc setup fn should have been called before calling this
export const getInTextCitations = (
  citationIDs: string[],
  modifiers?: CiteProcModifiers,
): string[] => {
  const inTextCitationItems: CiteProcRebuildProcessorOutput =
    citeproc.rebuildProcessorState(
      citationIDs.map((citationID) => ({
        citationID,
        citationItems: [{ id: citationID, ...modifiers }],
      })),
    );
  // TODO consider replacing '[NO_PRINTED_FORM]' items with undefined
  return inTextCitationItems
    .filter((item) => !item.includes('CSL STYLE ERROR: '))
    .map((item) => item[2]);
};

export const getCSLCitationStyle = (): { name: string; xml: string } => {
  return currentStyle;
};

export const getCitationLocation = (): CitationLocation => {
  if (citeproc && citeproc.cslXml.dataObj.attrs.class === 'note') {
    return CitationLocation.Footnote;
  } else {
    return CitationLocation.Intext;
  }
};

export const isCurrentStyleSortingBibliography = (): boolean => {
  return !!citeproc?.cslXml.dataObj.children
    .find((child: { name: string }) => child.name === 'bibliography')
    ?.children.find((child: { name: string }) => child.name === 'sort');
};

export default getCitations;
